import React, { useState, useMemo, useEffect, useRef } from 'react';
import { View } from 'react-native';
import Animated, {
  cancelAnimation,
  Easing,
  useSharedValue,
  useAnimatedStyle,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { VStack } from '@/components/gluestack/vstack';
import { HStack } from '@/components/gluestack/hstack';
import { Text } from '@/components/gluestack/text';
import { AccountService } from '~/services/domain/AccountService';
import { ISearchCardV6 } from '~/models/SearchCardV6';

// Threshold constants
export const FILTER_WARNING_THRESHOLD = 60;
export const FILTER_DANGER_THRESHOLD = 85;

const DEBOUNCE_DELAY = 1000;

interface MyTypeFilterSummaryProps {
  searchCard: ISearchCardV6;
  isEnabled: boolean;
  onHeightChange?: (height: number) => void;
}

export const MyTypeFilterSummary: React.FC<MyTypeFilterSummaryProps> = ({
  searchCard,
  isEnabled,
  onHeightChange,
}) => {
  const { t: tDefault } = useTranslation();
  const [debouncedSearchCard, setDebouncedSearchCard] = useState<ISearchCardV6>(searchCard);

  // Notify parent of height 0 when disabled
  useEffect(() => {
    if (!isEnabled) {
      onHeightChange?.(0);
    }
  }, [isEnabled, onHeightChange]);

  // Debounce searchCard changes for filter count
  useEffect(() => {
    if (!isEnabled) return;
    const handler = setTimeout(() => {
      setDebouncedSearchCard(searchCard);
    }, DEBOUNCE_DELAY);
    return () => clearTimeout(handler);
  }, [searchCard, isEnabled]);

  const { data: filterCount, isPending: isCounting } = useQuery({
    queryKey: ['myTypeFilterCount', debouncedSearchCard],
    queryFn: () => AccountService.getMyTypeFilterCount(debouncedSearchCard),
    enabled: isEnabled,
    placeholderData: (previousData) => previousData,
  });

  const filteredPercent = useMemo(() => {
    if (!filterCount || filterCount.TotalCount === 0) return 0;
    return Math.round(
      ((filterCount.TotalCount - filterCount.MatchingCount) / filterCount.TotalCount) * 100
    );
  }, [filterCount]);

  const animScale = useSharedValue(1);
  const progressValue = useSharedValue(filteredPercent);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: animScale.value }],
  }));

  const progressAnimatedStyle = useAnimatedStyle(() => ({
    width: `${progressValue.value}%`,
  }));

  const prevPercentRef = useRef(filteredPercent);
  useEffect(() => {
    if (filteredPercent !== prevPercentRef.current) {
      cancelAnimation(animScale);
      animScale.value = 1;
      animScale.value = withSequence(
        withTiming(1.12, {
          duration: 110,
          easing: Easing.out(Easing.quad),
        }),
        withTiming(1, {
          duration: 140,
          easing: Easing.inOut(Easing.quad),
        })
      );
      progressValue.value = withTiming(filteredPercent, { duration: 500 });
      prevPercentRef.current = filteredPercent;
    }
  }, [filteredPercent, animScale, progressValue]);

  const isVisible = isEnabled && !!filterCount && filteredPercent > 0;

  useEffect(() => {
    if (!isVisible) {
      onHeightChange?.(0);
    }
  }, [isVisible, onHeightChange]);

  if (!isVisible) {
    return null;
  }

  const isDanger = filteredPercent >= FILTER_DANGER_THRESHOLD;
  const isWarning = filteredPercent >= FILTER_WARNING_THRESHOLD;

  const bgColorClass = isDanger
    ? 'bg-error-50/60'
    : isWarning
      ? 'bg-warning-50/60'
      : 'bg-background-50/0';

  const textColorClass = isDanger ? 'text-error-600' : 'text-warning-600';
  const pillBgClass = isDanger ? 'bg-error-500' : isWarning ? 'bg-warning-500' : 'bg-success-500';

  const translatedFullPhrase = tDefault('myTypeFilter.filteredOutPercent', {
    percent: filteredPercent,
  });
  const percentString = `${filteredPercent}%`;
  const staticPhrasePart = translatedFullPhrase.replace(percentString, '').trim();

  return (
    <View
      onLayout={(e) => onHeightChange?.(e.nativeEvent.layout.height)}
      className={`p-4 items-center justify-center ${bgColorClass}`}>
      {/* Progress line indicator */}
      <View className="absolute top-0 left-0 right-0 h-1 bg-outline-100">
        <Animated.View 
          style={[{ height: '100%' }, progressAnimatedStyle]}>
          <View className={`h-full ${pillBgClass}`} />
        </Animated.View>
      </View>

      <VStack className="items-center gap-2">
        {isCounting && !filterCount ? (
          <Text size="lg" className="text-typography-700 font-bold text-center">
            {tDefault('common.loading')}
          </Text>
        ) : (
          <HStack className="items-center justify-center gap-2">
            <Animated.View 
              style={animatedStyle}>
              <View className={`px-2 py-0.5 rounded-full ${pillBgClass} shadow-sm`}>
                <Text size="lg" className="text-white font-black">
                  {percentString}
                </Text>
              </View>
            </Animated.View>
            <Text size="md" className="text-typography-800 font-bold">
              {staticPhrasePart}
            </Text>
          </HStack>
        )}
        {isWarning && (
          <Text 
            size="sm" 
            className={`text-center font-medium ${textColorClass}`}>
            {tDefault('myTypeFilter.filterWarning')}
          </Text>
        )}
      </VStack>
    </View>
  );
};
